import { memo, useCallback } from "react";
import { formatISO } from "date-fns";
import { capitalize } from "@common/helpers/string";
import { BillStatus as BillStatusEnum } from "@common/types/bill";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { BillStatus } from "./BillStatus";

const BILL_STATUS_ITEMS: Array<PickerItemData<BillStatusEnum>> = [
    BillStatusEnum.DRAFT,
    BillStatusEnum.OPEN,
    BillStatusEnum.PAID,
    BillStatusEnum.VOID,
].map((status) => ({
    value: status,
    label: capitalize(status),
    leading: <BillStatus status={status} />,
}));

interface BillStatusSelectProps {
    value: BillStatusEnum;
    onValueChange: (params: {
        status: BillStatusEnum;
        paidAt?: string | null;
    }) => void;
}

export const BillStatusSelect = memo(
    ({ value, onValueChange }: BillStatusSelectProps) => {
        const handleStatusChange = useCallback(
            (newStatus: BillStatusEnum | null) => {
                if (!newStatus) {
                    return;
                }

                if (newStatus === BillStatusEnum.PAID) {
                    onValueChange({
                        status: BillStatusEnum.PAID,
                        paidAt: formatISO(new Date(), {
                            representation: "date",
                        }),
                    });
                    return;
                }

                onValueChange({
                    status: newStatus,
                    paidAt: value === BillStatusEnum.PAID ? null : undefined,
                });
            },
            [onValueChange, value],
        );

        return (
            <Picker.Select
                items={BILL_STATUS_ITEMS}
                value={value}
                onValueChange={handleStatusChange}
                renderValue={() => <BillStatus status={value} showText />}
                chevron="none"
                trigger={<Picker.Button variant="secondary" size="sm" />}
            />
        );
    },
);

BillStatusSelect.displayName = "BillStatusSelect";
